import { messageService } from '@classes/messageService';

/** Что видит пользователь, пытаясь уйти, пока сообщение уходит в фоне */
export const PENDING_SEND_WARNING = 'Подождите! Идёт отправка файла.';

/** Метка копии записи истории, которую ставит сторож (см. `pushGuardEntry`) */
const GUARD_KEY = 'pendingSendGuard';

let pendingCount = 0;

/** Текст предупреждения для текущей задачи: у отправки файла и смены статусов он разный */
let currentWarning = PENDING_SEND_WARNING;

/** Идёт ли сейчас фоновая отправка — проверяют кнопки «обновить» и прочие уходы */
export const isSendPending = () => pendingCount > 0;

/**
 * Предупреждение о фоновой отправке; `true` — отправка идёт и уход надо отменить.
 * Для своих кнопок ухода: `if (warnIfSendPending()) return;`
 */
export const warnIfSendPending = () => {
    if (!isSendPending()) return false;
    messageService.sendWarning(currentWarning);
    return true;
};

// Перезагрузка и закрытие вкладки: свой текст браузер не покажет, только системный вопрос
const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
};

/**
 * «Назад» браузера (и аппаратный на Android): роутер собран на `<BrowserRouter>`,
 * поэтому `useBlocker` недоступен. На время отправки кладём в историю копию
 * текущей записи — «назад» снимает её, а страница остаётся той же.
 */
const pushGuardEntry = () => {
    const state: unknown = window.history.state;
    const baseState = typeof state === 'object' && state !== null ? state : {};
    window.history.pushState({ ...baseState, [GUARD_KEY]: true }, '', window.location.href);
};

const handlePopState = () => {
    if (!isSendPending()) return;
    messageService.sendWarning(currentWarning);
    pushGuardEntry();
};

const isGuardEntryOnTop = () => {
    const state: unknown = window.history.state;
    return typeof state === 'object' && state !== null && GUARD_KEY in state;
};

const startGuard = () => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    pushGuardEntry();
};

const stopGuard = () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('popstate', handlePopState);
    // Копия в истории больше не нужна: иначе первое «назад» после отправки
    // ничего бы не делало. Если пользователь успел уйти дальше — не трогаем
    if (isGuardEntryOnTop()) window.history.back();
};

/**
 * Выполняет задачу в фоне, не давая за это время перезагрузить страницу или
 * уйти «назад»: такой уход оборвал бы загрузку файла. Отправки могут
 * пересекаться — сторож снимается, когда закончилась последняя.
 */
export const runGuardedSend = async <T>(
    task: () => Promise<T>,
    warning: string = PENDING_SEND_WARNING
): Promise<T> => {
    currentWarning = warning;
    pendingCount += 1;
    if (pendingCount === 1) startGuard();

    try {
        return await task();
    } finally {
        pendingCount -= 1;
        if (pendingCount === 0) {
            stopGuard();
            currentWarning = PENDING_SEND_WARNING;
        }
    }
};
